import React, { useState } from "react";
import {
    Book, Search, Sparkles, Send, LineChart, Wallet, Shield, Rocket,
    ChevronRight, Zap, Users, HelpCircle,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { bl } from "@/lib/extrasApi";

const SECTIONS = [
    { key: "start", icon: Rocket, en: "Getting started", fr: "Démarrage rapide" },
    { key: "find", icon: Search, en: "FIND — Lead discovery", fr: "FIND — Trouver des leads" },
    { key: "enrich", icon: Sparkles, en: "ENRICH — Data enrichment", fr: "ENRICH — Enrichissement" },
    { key: "engage", icon: Send, en: "ENGAGE — Campaigns & Autopilot", fr: "ENGAGE — Campagnes & Autopilote" },
    { key: "analyze", icon: LineChart, en: "ANALYZE — Metrics", fr: "ANALYZE — Statistiques" },
    { key: "credits", icon: Wallet, en: "Credits & Billing", fr: "Crédits & Facturation" },
    { key: "refund", icon: Zap, en: "Bad lead? Get refunded", fr: "Mauvais lead ? Remboursé" },
    { key: "rgpd", icon: Shield, en: "GDPR / DPA", fr: "RGPD / DPA" },
    { key: "team", icon: Users, en: "Team & roles", fr: "Équipe & rôles" },
    { key: "faq", icon: HelpCircle, en: "FAQ", fr: "FAQ" },
];

const CONTENT_FR = {
    start: {
        title: "Démarrage en 5 minutes",
        blocks: [
            { h: "1. Crée ton workspace", p: "Inscription en 30s (email + mot de passe, ou Google). Tu reçois automatiquement 50 crédits offerts pour tester." },
            { h: "2. Importe 300 vrais leads Guyane", p: "Va sur FIND → Import → « One-click Guyane Starter ». En 20 secondes, la plateforme récupère 300-500 vraies entreprises via l'API officielle INSEE (data.gouv.fr). 100% RGPD-clean, 0 crédit consommé pour l'import." },
            { h: "3. Enrichis les emails", p: "Va sur ENRICH → sélectionne 20-30 leads → « Deviner » (IA, 1c/lead) pour un premier test, ou « Hunter.io » (3c/lead, 95% vérifiés) pour du sérieux." },
            { h: "4. Génère et envoie une campagne", p: "ENGAGE → « Nouvelle campagne » → wizard 3 étapes. GPT-5.2 rédige tes emails, tu valides, tu envoies." },
            { h: "5. Regarde les stats", p: "ANALYZE → tableau de bord temps réel : ouvertures, réponses, funnel, ROI." },
        ],
    },
    find: {
        title: "FIND — Trouver des leads",
        blocks: [
            { h: "Import INSEE (recommandé)", p: "Import → « One-click Guyane » : 300-500 vraies entreprises du département 973 (BTP, restaurants, commerce, tourisme, transport…). Ou import ciblé par code NAF/département." },
            { h: "AI Prospecting", p: "FIND → AI Prospect. Écris en langage naturel : « CTOs de startups SaaS Paris qui ont levé récemment » — GPT-5.2 convertit ta phrase en filtres et te sort les leads correspondants (2 crédits par recherche)." },
            { h: "Segments", p: "Sauvegarde n'importe quelle recherche comme segment (BTP Guyane 10-30 salariés, par exemple) et relance-la plus tard sans consommer de crédits." },
            { h: "Import CSV", p: "Colonne obligatoire : email OU (first_name + last_name + company_name). Format libre, mapping auto." },
        ],
    },
    enrich: {
        title: "ENRICH — Compléter les données",
        blocks: [
            { h: "Stratégie IA (Plan B) — 1c/lead", p: "GPT-5.2 devine l'email depuis le nom + société. 40-60% de délivrabilité. Utilise-le pour tester des volumes rapidement avant d'investir en Hunter." },
            { h: "Hunter.io (Plan A) — 3c/lead", p: "API Hunter réelle, emails vérifiés à 95%+. À utiliser pour tes cibles chaudes uniquement (économe en crédits)." },
            { h: "XCrawl phones (Plan C) — 3c/lead", p: "Scan Google SERP légal via XCrawl. Trouve les téléphones que l'INSEE n'a pas — 60-80% de succès sur PME françaises et guyanaises." },
        ],
    },
    engage: {
        title: "ENGAGE — Séquences & Autopilote",
        blocks: [
            { h: "Wizard campagne 3 étapes", p: "Nouvelle campagne → choisis segment/leads → GPT-5.2 rédige subject + body en 60-90 mots → prévisualise → lance. Chaque email IA coûte 1 crédit." },
            { h: "Séquences multi-steps", p: "Jusqu'à 5 étapes : J0 email, J3 bump, J7 rupture, J10 LinkedIn, J14 phone. A/B testing natif sur subject et body." },
            { h: "Autopilote IA", p: "Active l'autopilote (max 20 emails/jour en warm-up, 50/jour après 4 semaines). L'IA classifie les réponses automatiquement (positive/négative/OOO/neutre) et stoppe les envois sur les négatifs. Nécessite RESEND_API_KEY (envoi email)." },
        ],
    },
    analyze: {
        title: "ANALYZE — Mesurer ce qui compte",
        blocks: [
            { h: "Dashboard temps réel", p: "Emails envoyés, taux d'ouverture (open rate), taux de réponse (reply rate), rendez-vous bookés, coût par rendez-vous." },
            { h: "Funnel visuel", p: "De « leads révélés » à « client signé » en 5 étapes, avec taux de conversion à chaque marche." },
            { h: "Export PDF/CSV", p: "Rapports mensuels prêts à envoyer à ton équipe ou à un investisseur." },
        ],
    },
    credits: {
        title: "Crédits & Facturation",
        blocks: [
            { h: "Comment ça marche ?", p: "Pay-as-you-go. Pas d'engagement. Achète des packs de crédits (Stripe) et utilise-les sur toutes les fonctionnalités." },
            { h: "Coût des actions", p: "• Reveal email : 1c\n• Reveal phone : 2c\n• AI email guess : 1c\n• Hunter.io verified : 3c\n• XCrawl phone : 3c\n• AI prospecting search : 2c\n• AI email generation : 1c" },
            { h: "Packs disponibles", p: "500c à 19€, 2000c à 49€, 10000c à 199€. Facturation Stripe (LIVE mode). Reçus téléchargeables dans Billing." },
        ],
    },
    refund: {
        title: "Mauvais lead ? Tu es remboursé",
        blocks: [
            { h: "Le principe", p: "Tu paies 1c pour révéler un email. Si l'email est mauvais (fake, périmé, mauvaise personne), clique 👎 sur le lead → tu es remboursé automatiquement." },
            { h: "Ce qui est remboursé", p: "Tous les crédits dépensés sur CE lead : reveal email (1c) + reveal phone (2c) + enrich Hunter (3c) + enrich XCrawl (3c) + AI guess (1c)." },
            { h: "Plafond anti-abus", p: "Maximum 20% de ta dépense mensuelle peut être remboursée. Ça évite les demandes abusives tout en garantissant une vraie protection risk-free pour les leads défectueux." },
            { h: "Comment ça marche techniquement", p: "Clique 👎 sur un lead dans la table Enrich, ou dans la page détail du lead. Optionnellement, tu donnes une raison (fake email, mauvais rôle, entreprise fermée). Les crédits reviennent instantanément sur ton compte." },
        ],
    },
    rgpd: {
        title: "RGPD, DPA et conformité",
        blocks: [
            { h: "Sources de données", p: "Notre source principale est l'API officielle recherche-entreprises.api.gouv.fr (INSEE). 100% RGPD-clean, aucun scraping illégal, aucun achat de bases." },
            { h: "DPA (Article 28 RGPD)", p: "Requis par la plupart des acheteurs enterprise avant signature. Va sur Paramètres → « Télécharger le DPA (PDF) ». Le document est pré-signé par BOXEB2B et pré-rempli avec les infos de ton organisation." },
            { h: "Export & suppression", p: "Paramètres → Export RGPD (JSON complet de toutes tes données) ou Suppression définitive (irréversible, respecte l'Art. 17 RGPD)." },
            { h: "Sécurité", p: "TLS 1.3 en transit, AES-256 au repos, MongoDB Atlas UE (Francfort), séparation multi-tenant stricte, sauvegardes chiffrées quotidiennes." },
        ],
    },
    team: {
        title: "Équipe & rôles",
        blocks: [
            { h: "Invitations", p: "Team → « Inviter » → email + rôle. L'invité reçoit un lien magique pour rejoindre ton workspace." },
            { h: "Rôles disponibles", p: "• Admin : tout accès (facturation, suppression, invitations)\n• Member : lecture + écriture (leads, campagnes)\n• Viewer : lecture seule (idéal pour un manager ou un investisseur)" },
            { h: "Plans", p: "Free : 1 user · Starter : 3 users · Growth : 10 users · Enterprise : illimité + white-label." },
        ],
    },
    faq: {
        title: "FAQ",
        blocks: [
            { h: "C'est vraiment gratuit pour commencer ?", p: "Oui. 50 crédits offerts, aucune carte demandée. Tu peux importer 300+ leads, tester l'enrichissement IA et envoyer quelques emails sans payer." },
            { h: "Vous scrapez LinkedIn ?", p: "Non. Nous utilisons uniquement des sources légales : API INSEE, Hunter.io (API officielle), XCrawl (moteurs de recherche publics via proxy légal). Aucun scraping LinkedIn (illégal ET banni)." },
            { h: "Combien de temps pour signer mon 1er client ?", p: "60 jours en moyenne avec le workflow du Playbook (téléchargeable en /playbook). Semaine 1-2 : import + enrichissement. Semaine 3-4 : envoi + itération. Semaine 5-8 : booker + signer." },
            { h: "Puis-je utiliser mon propre domaine email ?", p: "Phase 6+ prévoit une option SMTP custom. Pour l'instant, l'envoi passe par notre infra Resend (nécessite ta clé Resend API personnelle)." },
            { h: "Vous supportez d'autres pays que la France ?", p: "Oui — Union européenne complète (RGPD-first), avec un focus fort DOM-COM (Guyane, Antilles, Réunion, Mayotte). Roadmap : multilingue 12 langues Phase 8+." },
            { h: "Puis-je revendre BOXEB2B en marque blanche ?", p: "Oui, sur le plan Enterprise (499€/mois). Contacte-nous à contact@boxe-b2b.com." },
        ],
    },
};

const CONTENT_EN = {
    start: {
        title: "5-minute quickstart",
        blocks: [
            { h: "1. Create your workspace", p: "30s signup (email + password, or Google). You get 50 free credits to test." },
            { h: "2. Import 300 real French Guiana leads", p: "FIND → Import → « One-click Guyane Starter ». In 20s you get 300-500 real companies via the official INSEE API. 100% GDPR-clean, no credits spent." },
            { h: "3. Enrich emails", p: "ENRICH → select 20-30 leads → « Guess » (AI, 1c/lead) or « Hunter.io » (3c/lead, 95% verified) for hot targets." },
            { h: "4. Generate + send a campaign", p: "ENGAGE → « New campaign » → 3-step wizard. GPT-5.2 writes the emails, you approve, you send." },
            { h: "5. Track results", p: "ANALYZE → real-time dashboard: opens, replies, funnel, ROI." },
        ],
    },
    find: { title: "FIND — Lead discovery", blocks: [
        { h: "INSEE import (recommended)", p: "300-500 real companies from any French department. Free, GDPR-clean." },
        { h: "AI Prospecting", p: "Write in natural language: « CTOs of SaaS startups in Paris that raised recently ». GPT-5.2 turns it into filters (2c/query)." },
        { h: "Segments", p: "Save any search, re-run later without spending credits." },
        { h: "CSV import", p: "Required column: email OR (first_name + last_name + company)." },
    ]},
    enrich: { title: "ENRICH — Complete data", blocks: [
        { h: "AI (Plan B) — 1c/lead", p: "GPT-5.2 guesses email patterns. 40-60% deliverability. Use for cheap volume tests." },
        { h: "Hunter.io (Plan A) — 3c/lead", p: "Real API, verified 95%+. Use on hot targets only." },
        { h: "XCrawl phones (Plan C) — 3c/lead", p: "Legal Google SERP scan. Finds phones INSEE doesn't have." },
    ]},
    engage: { title: "ENGAGE — Sequences & Autopilot", blocks: [
        { h: "Campaign wizard", p: "GPT-5.2 writes 60-90 word emails (1c each). A/B testing built-in." },
        { h: "Multi-step sequences", p: "D0 email, D3 bump, D7 breakup, D10 LinkedIn, D14 phone." },
        { h: "AI Autopilot", p: "Max 20 emails/day warm-up. AI auto-classifies replies. Needs RESEND_API_KEY." },
    ]},
    analyze: { title: "ANALYZE — Track what matters", blocks: [
        { h: "Real-time dashboard", p: "Emails sent, open rate, reply rate, meetings booked, cost per meeting." },
        { h: "Funnel", p: "5-stage conversion funnel with rates at each step." },
        { h: "PDF/CSV export", p: "Monthly reports ready to share." },
    ]},
    credits: { title: "Credits & Billing", blocks: [
        { h: "How it works", p: "Pay-as-you-go. No commitment. Buy credit packs via Stripe." },
        { h: "Action costs", p: "• Reveal email: 1c · Reveal phone: 2c · AI guess: 1c · Hunter: 3c · XCrawl: 3c · Prospect search: 2c · AI email gen: 1c" },
        { h: "Packs", p: "500c €19, 2000c €49, 10000c €199. LIVE Stripe. Invoices downloadable." },
    ]},
    refund: { title: "Bad lead? Get refunded", blocks: [
        { h: "Principle", p: "You pay 1c to reveal an email. If it's bad (fake, outdated, wrong person), click 👎 on the lead → auto-refunded." },
        { h: "What's refunded", p: "Every credit spent on THIS lead: email + phone + enrichment (Hunter + XCrawl + AI)." },
        { h: "Anti-abuse cap", p: "Max 20% of your monthly spend can be refunded." },
        { h: "How to use", p: "Click 👎 on any lead in Enrich table or lead detail. Optional reason. Credits back instantly." },
    ]},
    rgpd: { title: "GDPR, DPA & compliance", blocks: [
        { h: "Data sources", p: "Primary source: official recherche-entreprises.api.gouv.fr (INSEE). 100% GDPR-clean." },
        { h: "DPA (GDPR Art. 28)", p: "Required by enterprise buyers. Settings → « Download DPA (PDF) ». Pre-signed by BOXEB2B, pre-filled with your org." },
        { h: "Export & delete", p: "Settings → GDPR export (JSON) or definitive delete (irreversible, Art. 17)." },
        { h: "Security", p: "TLS 1.3, AES-256 at rest, EU-hosted MongoDB, multi-tenant isolation." },
    ]},
    team: { title: "Team & roles", blocks: [
        { h: "Invites", p: "Team → « Invite » → email + role. Recipient gets a magic link." },
        { h: "Roles", p: "Admin: full · Member: read/write · Viewer: read-only." },
        { h: "Plans", p: "Free: 1 · Starter: 3 · Growth: 10 · Enterprise: unlimited + white-label." },
    ]},
    faq: { title: "FAQ", blocks: [
        { h: "Really free to start?", p: "Yes. 50 credits, no card asked." },
        { h: "Do you scrape LinkedIn?", p: "Never. Only legal sources: INSEE, Hunter.io, XCrawl." },
        { h: "How long to sign 1st client?", p: "60 days on average with the Playbook workflow (/playbook)." },
        { h: "Can I use my own email domain?", p: "Phase 6+: custom SMTP. For now: Resend infrastructure (needs your Resend API key)." },
        { h: "Countries?", p: "Full EU (GDPR-first), strong DOM-COM focus. 12-language roadmap Phase 8+." },
        { h: "White-label?", p: "Enterprise plan (€499/month). Contact contact@boxe-b2b.com." },
    ]},
};

export default function Docs() {
    const { locale } = useI18n();
    const [active, setActive] = useState("start");
    const content = (locale === "fr" ? CONTENT_FR : CONTENT_EN)[active];

    return (
        <div className="px-6 lg:px-10 py-6 max-w-6xl">
            <div className="mb-6">
                <div className="flex items-center gap-2 text-xs text-brand-cyan font-mono uppercase tracking-widest mb-2">
                    <Book size={13} /> {bl("Documentation", "Documentation", locale)}
                </div>
                <h1 className="font-display font-black text-3xl tracking-tighter">
                    {bl("How to use BOXEB2B", "Comment utiliser BOXEB2B", locale)}
                </h1>
                <p className="mt-1 text-sm text-zinc-500 max-w-3xl">
                    {bl(
                        "Everything you need to go from signup to signed clients in 60 days. Short, honest, no fluff.",
                        "Tout ce qu'il te faut pour aller de l'inscription au 1er client signé en 60 jours. Court, honnête, sans blabla.",
                        locale,
                    )}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
                {/* Nav */}
                <nav className="rounded-2xl bg-brand-surface border border-white/8 p-2 h-fit sticky top-6">
                    {SECTIONS.map(({ key, icon: Icon, en, fr }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setActive(key)}
                            data-testid={`docs-nav-${key}`}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition ${
                                active === key
                                    ? "bg-brand-indigo/15 text-white border-l-2 border-brand-indigo"
                                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                            }`}
                        >
                            <Icon size={14} className="shrink-0" />
                            <span className="truncate">{locale === "fr" ? fr : en}</span>
                            {active === key && <ChevronRight size={12} className="ml-auto text-brand-indigo" />}
                        </button>
                    ))}
                </nav>

                {/* Body */}
                <div className="rounded-2xl bg-brand-surface border border-white/8 p-8" data-testid="docs-content">
                    <h2 className="font-display font-black text-2xl tracking-tighter mb-6">{content.title}</h2>
                    <div className="space-y-6">
                        {content.blocks.map((b, i) => (
                            <div key={i} className="border-l-2 border-brand-cyan/40 pl-4">
                                <div className="text-sm font-semibold text-brand-cyan mb-1.5">{b.h}</div>
                                <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{b.p}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 pt-6 border-t border-white/8 text-xs text-zinc-500">
                        {bl(
                            "Still stuck? Email contact@boxe-b2b.com — we reply within 24h.",
                            "Encore bloqué ? Email contact@boxe-b2b.com — réponse sous 24h.",
                            locale,
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
